<script setup lang="ts">
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import { difference, intersection } from "lodash-es";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import LeMethodTitles from "@/components/search-results/LeMethodTitles.vue";
import { MatomoHelper } from "@/helpers";
import { useSearchStore } from "@/stores";
import { type EmptyResults, type FuzzyResults, MatomoEventAction, MatomoEventCategory } from "@/types";

const props = defineProps<{ results: FuzzyResults | EmptyResults }>();

const emit = defineEmits<{
  (
    event: "search",
    search: {
      query: string;
    }
  ): void;
}>();

const { t } = useI18n();
const searchStore = useSearchStore();

const dictionariesWithResultAmount = computed(() => searchStore.dictionariesWithPrioritizedResults.length);

const shouldDisplayOtherText = computed(() => dictionariesWithResultAmount.value > 0);

const onSearch = (query: string) => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Search,
    MatomoEventAction.ClickFuzzySuggestion,
    "Clicked button for suggestion"
  );

  emit("search", { query });
};

const alternativeDictionaryMethodIds = computed<(string | undefined)[]>(() =>
  difference(searchStore.selectedMethodIds, [searchStore.direction])
);
const hasAlternativeDictionaryResult = computed<boolean>(
  () => intersection(alternativeDictionaryMethodIds.value, searchStore.results?.methodIdsWithResult ?? []).length > 0
);

const metadataTitle = computed<string>(() => {
  if (!searchStore.selectedDictionaryMetadata) {
    return "";
  }

  const metadata = searchStore.selectedDictionary?.metadata.find(
    (m) => searchStore.selectedDictionaryMetadata[0] === m.name
  );

  if (!metadata) {
    return "";
  }

  return metadata.title;
});
</script>

<template>
  <div class="le-fuzzy-results">
    <LeHeading tag="h2" size="m" class="le-fuzzy-header">
      <span v-if="dictionariesWithResultAmount === 0">{{ t("le-fuzzy-results.no-results") }}</span>
      <span v-else-if="hasAlternativeDictionaryResult">
        <i18n-t tag="span" keypath="le-fuzzy-results.no-results-in-direction">
          <template #methodTitle>
            <LeMethodTitles :method-id="searchStore.direction ?? ''" inline />
          </template>
        </i18n-t>
      </span>
      <span v-else-if="metadataTitle.length">
        {{ t("le-fuzzy-results.no-results-in-metadata-method", { metadataTitle }) }}
      </span>
      <span v-else>{{ t("le-fuzzy-results.no-results-in-dictionary") }}</span>
    </LeHeading>

    <p v-if="shouldDisplayOtherText" class="le-fuzzy-text">
      {{ t("le-fuzzy-results.text-see-other", dictionariesWithResultAmount) }}
    </p>

    <template v-if="props.results.type === 'fuzzy'">
      <i18n-t
        v-if="searchStore.selectedDictionary"
        tag="p"
        class="le-fuzzy-text"
        keypath="le-fuzzy-results.text-above-fuzzies-using-dictionary"
      >
        <template #dictionary>
          <span class="le-fuzzy-text__dictionary">{{ searchStore.selectedDictionary?.title }}</span>
        </template>
      </i18n-t>

      <p v-else class="le-fuzzy-text">{{ t("le-fuzzy-results.text-above-fuzzies") }}</p>

      <button
        v-for="query in props.results.queries"
        :key="query"
        type="button"
        class="le-fuzzy-query-item"
        tabindex="-1"
        @click="onSearch(query)"
      >
        <span class="le-fuzzy-query-item__text">{{ query }}</span>
        <LeButton color="neutral" size="small" aria-hidden="true" @click="onSearch(query)">
          {{ t("le-fuzzy-results.button") }}
        </LeButton>
      </button>
    </template>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-fuzzy-header {
  margin-bottom: 25px;
}

.le-fuzzy-text {
  .body-text("s");
  margin-bottom: 20px;

  &__dictionary {
    font-weight: bold;
  }
}

.le-fuzzy-query-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;
  padding: 8px 14px;
  border: 0;
  border-radius: 4px;
  background-color: @colors[@neutrals][100];
  box-shadow: @shadows[floating-default];
  gap: 40px;

  #media-breakpoint-up(md, {
    padding: 14px 40px;
  });

  &:hover {
    background-color: @colors[@secondary][100];
  }

  &__text {
    color: @colors[@neutrals][500];
    font-size: 1rem;
    font-weight: 600;
  }
}
</style>
